/**
 * Clients React Query Hooks
 * 
 * This module provides React Query hooks for the Clients module:
 * - useClientsList: List with filters
 * - useClient: Single client detail
 * - useClientMutations: CRUD operations with cache invalidation
 * 
 * All queries include tenant_id in the query key to prevent cross-tenant cache bleed.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Hook: useClientsList
 * Fetches clients list with optional date filtering
 */
export function useClientsList({
  dateField = null,
  dateOperator = null,
  dateValue = null,
  dateValueTo = null,
  enabled = true,
} = {}) {
  const { selectedTenantId } = useTenant();
  
  // Create a stable key from date filters
  const filtersKey = JSON.stringify({ dateField, dateOperator, dateValue, dateValueTo });
  
  return useQuery({
    queryKey: queryKeys.clients.list(selectedTenantId, filtersKey),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateField) params.append("date_field", dateField);
      if (dateOperator) params.append("date_operator", dateOperator);
      if (dateValue) params.append("date_value", dateValue);
      if (dateValueTo) params.append("date_value_to", dateValueTo);
      
      const queryString = params.toString();
      const url = queryString ? `/clients?${queryString}` : "/clients";
      const res = await api.get(url);
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook: useClient
 * Fetches a single client by ID with related lead and appointment data
 */
export function useClient(clientId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
    queryFn: async () => {
      const res = await api.get(`/clients/${clientId}`);
      return res.data || { client: null, lead: null, latest_appointment: null };
    },
    enabled: enabled && Boolean(clientId),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useClientByLead
 * Fetches client data by lead ID
 */
export function useClientByLead(leadId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: [...queryKeys.clients.all(selectedTenantId), "by-lead", leadId],
    queryFn: async () => {
      const res = await api.get(`/clients/by-lead/${leadId}`);
      return res.data;
    },
    enabled: enabled && Boolean(leadId),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook: useClientMutations
 * Provides mutation functions for clients with cache invalidation
 */
export function useClientMutations() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  // Update client
  const updateClient = useMutation({
    mutationFn: async ({ clientId, data }) => {
      const res = await api.put(`/clients/${clientId}`, data);
      return res.data;
    },
    onSuccess: (data, { clientId }) => {
      // Update the specific client in cache
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(selectedTenantId),
      });
    },
  });
  
  // Update product status (via sold_payloads)
  const updateProductStatus = useMutation({
    mutationFn: async ({ clientId, productIndex, data }) => {
      const res = await api.put(`/clients/${clientId}/products/${productIndex}`, data);
      return res.data;
    },
    onSuccess: (data, { clientId }) => {
      // Invalidate the specific client
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
      });
      // Invalidate the clients list - this ensures status changes show immediately
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(selectedTenantId),
      });
    },
  });
  
  // Delete client
  const deleteClient = useMutation({
    mutationFn: async (clientId) => {
      await api.delete(`/clients/${clientId}`);
      return clientId;
    },
    onSuccess: (clientId) => {
      // Remove the client from cache
      queryClient.removeQueries({
        queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(selectedTenantId),
      });
    },
  });
  
  // Bulk delete clients
  const bulkDeleteClients = useMutation({
    mutationFn: async (clientIds) => {
      await Promise.all(clientIds.map((id) => api.delete(`/clients/${id}`)));
      return clientIds;
    },
    onSuccess: (clientIds) => {
      // Remove each client from cache
      clientIds.forEach((clientId) => {
        queryClient.removeQueries({
          queryKey: queryKeys.clients.detail(selectedTenantId, clientId),
        });
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(selectedTenantId),
      });
    },
  });
  
  return {
    updateClient,
    updateProductStatus,
    deleteClient,
    bulkDeleteClients,
  };
}

/**
 * Hook: useInvalidateClients
 * Returns a function to invalidate all clients-related queries
 */
export function useInvalidateClients() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.clients.all(selectedTenantId),
    });
  };
}

export default {
  useClientsList,
  useClient,
  useClientByLead,
  useClientMutations,
  useInvalidateClients,
};
